import type { TTree } from 'components/tree/types'
import type { FindexAssetMap, FindexInvestmentMap } from '@/utils/asset/map'
import { notNullish } from '@vueuse/core'
import { getRefId, type FindexInvestment, type FullInvestment } from 'findef'
import { uniqueBy } from 'math'
import type { FunctionalComponent } from 'vue'

export type CreateTreeNodeProps = {
  invMap: FindexInvestmentMap
  assMap: FindexAssetMap
  investment: FindexInvestment | FullInvestment
  rootInvestment?: FindexInvestment | FullInvestment
  nodeBody?: (investment: FindexInvestment | FullInvestment) => FunctionalComponent<any>
  tooltipBody?: (investment: FindexInvestment | FullInvestment) => FunctionalComponent<any>;
}

export const createTreeNode = (props: CreateTreeNodeProps): TTree => {
  const seen = new Map<string, boolean>()

  const create = (
    invMap: FindexInvestmentMap,
    assMap: FindexAssetMap,
    inv: FindexInvestment | FullInvestment,
    depth: number = 0
  ): TTree => {
    const asset = inv.asset
    const id = getRefId(inv)

    const childrenInvestments = seen.has(id) ? [] : uniqueBy(
      (inv.childrenIds || []).map((id) => invMap[getRefId(id)]).filter(notNullish),
      (it) => getRefId(it)
    )

    seen.set(id, true)

    const root = props.rootInvestment
    const rootId = root ? getRefId(root) : undefined

    return {
      name: asset.name,
      showName: false,
      id: id,
      expanded: id === rootId,
      children: childrenInvestments.map((child) => {
        return create(invMap, assMap, child, depth + 1)
      }),
      body: props.nodeBody ? props.nodeBody(inv) : undefined,
      tooltip: {
        noPadding: true,
        variant: 'white',
        body: props.tooltipBody ? props.tooltipBody(inv) : undefined
      }
    }
  }
  return create(props.invMap, props.assMap, props.investment, 0)
}

export const createTree = (props: {
  name: string
  rootInvestment?: FindexInvestment | FullInvestment
  investments: Array<FindexInvestment | FullInvestment>
  nodeProps: Omit<CreateTreeNodeProps, 'investment'>
  nodeBody?: () => FunctionalComponent<any>
}): TTree => {
  const name = props.name
  const root = props.rootInvestment
  const childInvestments = root ? [root] : props.investments

  if (root && childInvestments.length === 1)
    return createTreeNode({
      ...props.nodeProps,
      investment: childInvestments[0]
    })

  return {
    name: name,
    expanded: true,
    showName: false,
    children: childInvestments.map((inv) => {
      return createTreeNode({
        ...props.nodeProps,
        invMap: props.nodeProps.invMap || {},
        assMap: props.nodeProps.assMap || {},
        investment: inv
      })
    }),
    body: props.nodeBody ? props.nodeBody() : undefined
  }
}
